import { Directive, ElementRef, HostListener, Renderer2, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const MAX_CENTS = 9999; // tope de 99,99
const NAVIGATION_KEYS = [
  'Tab',
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
];

/**
 * Input de moneda estilo "caja registradora": los dígitos se acumulan de
 * derecha a izquierda como centavos (escribir 543 muestra "5,43"), con tope
 * en 99,99.
 */
@Directive({
  selector: '[appCentsCurrencyInput]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CentsCurrencyInputDirective),
      multi: true,
    },
  ],
})
export class CentsCurrencyInputDirective implements ControlValueAccessor {
  private digits = '';
  private onChange: (value: number) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly el: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
  ) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (NAVIGATION_KEYS.includes(event.key)) {
      return;
    }

    event.preventDefault();

    if (event.key === 'Backspace' || event.key === 'Delete') {
      this.digits = this.digits.slice(0, -1);
    } else if (/^[0-9]$/.test(event.key) && this.digits.length < 4) {
      this.digits += event.key;
    }

    this.emitValue();
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedDigits = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    for (const digit of pastedDigits) {
      if (this.digits.length < 4) {
        this.digits += digit;
      }
    }
    this.emitValue();
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  writeValue(value: number | null): void {
    const cents = this.toCents(value ?? 0);
    this.digits = cents === 0 ? '' : cents.toString();
    this.render(cents);
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  private emitValue(): void {
    const cents = this.digits === '' ? 0 : parseInt(this.digits, 10);
    this.render(cents);
    this.onChange(cents / 100);
  }

  private toCents(value: number): number {
    const cents = Math.round(value * 100);
    return Math.min(Math.max(cents, 0), MAX_CENTS);
  }

  private render(cents: number): void {
    const padded = cents.toString().padStart(3, '0');
    const decimalPart = padded.slice(-2);
    const integerPart = parseInt(padded.slice(0, -2), 10).toString();
    this.renderer.setProperty(this.el.nativeElement, 'value', `${integerPart},${decimalPart}`);
  }
}
